import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { ProductCardText, hideStrikethroughPrice } from '@iheartjane/menu';
import {
  Button,
  Card,
  CheckIcon,
  DismissIcon,
  JaneGoldIcon,
  Modal,
  Popover,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import compact from 'lodash-es/compact';
import type { SyntheticEvent } from 'react';
import { forwardRef, useCallback, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Form, useSubmit } from 'react-router';

import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { LinkWithBasename } from '@jane/bloom/components/linkWithBasename';
import { ProductCategoryLabel } from '@jane/bloom/components/productCategoryLabel/productCategoryLabel';
import { useMarketplaceCart } from '@jane/bloom/hooks/cart/useMarketplaceCart';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackProductListingTap } from '@jane/bloom/services/tracking/track';
import type { BrandSpecials } from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import { getGoldLabel } from '@jane/bloom/utils/badges/getGoldLabel';
import { mergeCartItem } from '@jane/bloom/utils/cart/mergeCartItem';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import type { ShouldShowWeightFilter } from '@jane/bloom/utils/specials/shouldShowWeight';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import { hasSpecialApplied as hasSpecial } from '@jane/bloom/viewModels/getPrice';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';
import type { AlgoliaProduct } from '@jane/search/types';

import { AddToCartLabel } from './components/addToCartLabel';
import { SelectWeightForm } from './components/selectWeightForm';
import { SpecialBadge } from './components/specialBadge';
import styles from './productCard.module.css';

export type ProductCardProps = {
  brandSpecials: BrandSpecials;
  customRowId?: number;
  isPlacement?: boolean;
  isSponsored?: boolean;
  maxWeightOption?: number | null;
  preloaded?: boolean;
  product: ProductSearchResultViewModel;
  selected?: () => void;
  smartProductInstance?: SmartSortProduct<AlgoliaProduct>;
  weightFilter?: ShouldShowWeightFilter;
};

export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  function ProductCard(
    {
      brandSpecials,
      customRowId,
      isPlacement,
      isSponsored,
      maxWeightOption,
      preloaded,
      product,
      selected,
      smartProductInstance,
      weightFilter,
    },
    ref
  ) {
    const { fulfillmentMode, pinnedStore } = useStoreSwitcher();

    const formattedWeightKeys = Object.keys(product.availableWeightsFormatted);
    const selectedWeight =
      formattedWeightKeys[0] ?? product.available_weights?.[0] ?? 'each';

    const hasSpecialApplied = hasSpecial(product);

    const price =
      product.availableWeightsFormatted[selectedWeight ?? 'each']
        ?.priceFormatted;
    const priceBeforeDiscounts =
      product.availableWeightsFormatted[selectedWeight ?? 'each']
        ?.fullPriceFormatted;
    const priceIsDiscounted =
      priceBeforeDiscounts && price !== priceBeforeDiscounts;

    const { cart, featureFlags, shoppingPreferences } = useRootLoaderData();
    const submit = useSubmit();
    const {
      fetcher: { state },
      formArgs,
    } = useAddToCartConfig({
      product,
      productId: product.product_id,
      quantity: 1,
      rowId: customRowId,
      selectedWeight,
    });
    const { createCart, deleteCart, newCart } = useMarketplaceCart();

    const isAddingToCart = state === 'submitting';
    const countInCart = useOptimisticBagSize(product.product_id);
    const atMaxQuantity =
      getMaxCartQuantity({
        cart,
        currentProduct: product,
        currentWeight: selectedWeight,
        storeId: shoppingPreferences?.storeId,
      }) === 0;
    const [isWeightFormOpen, setIsWeightFormOpen] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const onSdkProductClick = useCallback(
      (addToCart = false) => {
        smartProductInstance?.click({ isAddToCart: addToCart });
      },
      [smartProductInstance]
    );

    const onSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      onSdkProductClick(true);

      // if cart items in the cart are from a different from the current store, prevent the add to cart action and show warning modal
      const hasPreviousStoreProducts = cart.storeId
        ? cart.storeId !== shoppingPreferences?.storeId
        : false;

      if (hasPreviousStoreProducts) {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const count = formData.get('count');
        const priceId = formData.get('priceId');
        const productId = formData.get('productId');

        if (shoppingPreferences) {
          createCart({
            products: mergeCartItem(
              {
                count: Number(count),
                price_id: priceId ? formatWeight(String(priceId)) : 'each',
                product_id: Number(productId),
              },
              cart.items
            ),
            reservationMode: shoppingPreferences.reservationMode,
            storeId: shoppingPreferences.storeId,
          });
        }

        setShowWarningModal(true);
      }
    };

    const confirmStartNewBag = () => {
      // close the modal
      setShowWarningModal(false);

      // replace the current cart with the new cart
      if (newCart) {
        submit({ shouldReplaceCart: 'true' }, formArgs);
      }
    };

    const handleCartWarningClose = () => {
      // close the modal
      setShowWarningModal(false);

      //delete the pending cart
      if (newCart) {
        deleteCart({ cartUuid: newCart?.cart.uuid });
      }
    };

    const janeGoldLabel = product.applicable_brand_special_ids
      ? getGoldLabel({
          applicableBrandSpecialIds: product.applicable_brand_special_ids,
          brandSpecials,
          lowestAvailablePriceVal:
            product.lowest_available_price &&
            'val' in product.lowest_available_price
              ? product.lowest_available_price.val
              : undefined,
        })
      : null;

    const { ref: impressionRef } = useInView({
      onChange: (inView) => {
        if (inView) {
          smartProductInstance?.impress();
        }
      },
      threshold: 0.5,
    });

    return (
      <div
        ref={ref}
        className={clsx(styles['productCard'], {
          [styles['productCardPlacement']]: isPlacement,
        })}
        data-testid="product-card"
      >
        <BagTransferModal
          open={showWarningModal}
          onConfirm={confirmStartNewBag}
          onRequestClose={handleCartWarningClose}
          newBag={newCart}
        />

        <LinkWithBasename
          ref={impressionRef}
          to={`products/${smartProductInstance?.attributes.product_id ?? product.product_id}/${product.unique_slug ?? product.id}`}
          className="focus-visible"
          relative="route"
          onClick={() => {
            if (selected) {
              selected();
            }
            trackProductListingTap(
              {
                fulfillmentMode,
                product,
                reservationMode: shoppingPreferences?.reservationMode,
                storeId: pinnedStore?.id ?? shoppingPreferences?.storeId,
              },
              featureFlags
            );
          }}
        >
          <div className={styles['productCardContainer']}>
            <Card
              height="100%"
              width="100%"
              flat
              className={styles['productCardImage']}
            >
              <div className={styles['badges']}>
                {product.shouldShowGold && janeGoldLabel && (
                  <Tag
                    background="gold"
                    color="grays-black"
                    icon={<JaneGoldIcon size="sm" />}
                    label={janeGoldLabel}
                    className={styles['janeGoldBadge']}
                  />
                )}
                {product.specialLabel && (
                  <SpecialBadge label={product.specialLabel} />
                )}
              </div>
              <Card.Image
                alt={product.name}
                src={product.image_urls?.[0]}
                srcSet={product.imageSrcSet?.srcSet}
                data-testid={product.unique_slug ?? ''}
                loading={preloaded ? 'eager' : 'lazy'}
              />
            </Card>
          </div>

          <ProductCardText
            brandInfo={compact([product.brand, product.brand_subtype]).join(
              ' '
            )}
            dosageInfo={product.dosageAmountPackInfo}
            hasMultipleWeightDiscount={
              Boolean(hasSpecialApplied) &&
              Boolean(priceIsDiscounted) &&
              product.hasMultipleWeights
            }
            isSponsored={isSponsored}
            name={product.name}
            potencyInfo={
              product.basePotencyLabResults ?? product.potencyLabResults
            }
            ratingSummary={product.aggregateRatingAndCount}
            renderProductCategoryLabel={({ className }) => (
              <ProductCategoryLabel
                category={product.category}
                className={className}
                kind={product.kind}
              />
            )}
            px={isPlacement ? 8 : 0}
          />
        </LinkWithBasename>
        <div className={styles['priceAndAddToCartContainer']}>
          <div
            className={clsx(styles['productCardPrice'], {
              [styles['productCardPriceMultipleWeights']]:
                product.hasMultipleWeights,
            })}
          >
            {price && (
              <Typography
                variant="body-bold"
                data-testid="product-price"
                color="text-main"
                className={clsx({
                  [styles['productCardDealsText']]: hasSpecialApplied,
                })}
              >
                {product.discountedPriceLabel}
              </Typography>
            )}
            {hasSpecialApplied &&
              priceBeforeDiscounts &&
              priceIsDiscounted &&
              !hideStrikethroughPrice(pinnedStore?.state || '') && (
                <Typography color="grays-mid" variant="mini" strikeThrough>
                  {product.priceLabel}
                </Typography>
              )}
          </div>
          {product.hasMultipleWeights ? (
            <ClientOnly>
              {(isMobile) =>
                isMobile ? (
                  <Popover
                    mt={4}
                    targetWidth="100%"
                    label="Select weight"
                    closeOnTargetClick={false}
                    target={
                      <Button
                        variant="primary"
                        full
                        mt="auto"
                        disabled={isAddingToCart || atMaxQuantity}
                        onClick={() => onSdkProductClick(false)}
                        startIcon={
                          isAddingToCart && (
                            <CheckIcon
                              className={styles['textContrastColor']}
                            />
                          )
                        }
                        label={
                          <AddToCartLabel
                            atMaxQuantity={atMaxQuantity}
                            countInCart={countInCart}
                            hasMultipleWeights
                            isBeingAddedToCart={isAddingToCart}
                          />
                        }
                        className={styles['addToCartButton']}
                      />
                    }
                  >
                    {({ closePopover }) => (
                      <Popover.Content label="Select weight">
                        <div className={styles['productCardPopoverHeader']}>
                          <Button.Icon
                            icon={<DismissIcon />}
                            label="close"
                            onClick={closePopover}
                          />
                          <Typography
                            variant="header-bold"
                            textAlign="center"
                            color="text-main"
                          >
                            Add to bag
                          </Typography>
                        </div>
                        <SelectWeightForm
                          maxWeightOption={maxWeightOption}
                          product={product}
                          weightFilter={weightFilter}
                          onSubmit={onSubmit}
                          onSuccess={closePopover}
                          smartProductInstance={smartProductInstance}
                        />
                      </Popover.Content>
                    )}
                  </Popover>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      full
                      mt={4}
                      disabled={isAddingToCart || atMaxQuantity}
                      startIcon={
                        isAddingToCart && (
                          <CheckIcon className={styles['textContrastColor']} />
                        )
                      }
                      label={
                        <AddToCartLabel
                          atMaxQuantity={atMaxQuantity}
                          countInCart={countInCart}
                          hasMultipleWeights
                          isBeingAddedToCart={isAddingToCart}
                        />
                      }
                      className={styles['addToCartButton']}
                      onClick={() => {
                        onSdkProductClick(false);
                        setIsWeightFormOpen(true);
                      }}
                    />

                    <Modal
                      open={isWeightFormOpen}
                      onRequestClose={() => setIsWeightFormOpen(false)}
                      overlayClose
                      variant="dialogue"
                    >
                      <Modal.Header
                        title="Add to bag"
                        textAlign="center"
                        branded={false}
                      />
                      <Modal.Content>
                        <SelectWeightForm
                          maxWeightOption={maxWeightOption}
                          product={product}
                          weightFilter={weightFilter}
                          onSubmit={onSubmit}
                          onSuccess={() => setIsWeightFormOpen(false)}
                          smartProductInstance={smartProductInstance}
                        />
                      </Modal.Content>
                    </Modal>
                  </>
                )
              }
            </ClientOnly>
          ) : (
            <Form
              name={`${product.id}-add-to-cart`}
              className={styles['productCardAddToCartForm']}
              onSubmit={onSubmit}
              {...formArgs}
            >
              <input type="hidden" name="productId" value={product.id} />
              <input type="hidden" name="priceId" value={selectedWeight} />
              <input type="hidden" name="count" value={1} />

              <Button
                variant="primary"
                full
                disabled={isAddingToCart || atMaxQuantity}
                startIcon={
                  isAddingToCart && (
                    <CheckIcon className={styles['textContrastColor']} />
                  )
                }
                label={
                  <AddToCartLabel
                    atMaxQuantity={atMaxQuantity}
                    countInCart={countInCart}
                    hasMultipleWeights={false}
                    isBeingAddedToCart={isAddingToCart}
                  />
                }
                className={styles['addToCartButton']}
                data-testid="add-to-cart"
                type="submit"
              />
            </Form>
          )}
        </div>
      </div>
    );
  }
);
